import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  House,
  Calendar,
  Camera,
  FileText,
  Users,
  CurrencyDollar,
  ChatCircle,
  CheckCircle,
  Clock,
  MapPin,
  Star,
  PaperPlaneTilt,
  CaretDown,
  CaretUp,
  Eye,
  Download,
  Warning,
  ShieldCheck,
  Handshake,
  SpinnerGap,
  SignOut,
  User,
  Lock,
  Envelope,
  Phone,
  ArrowRight,
  Plus,
  X,
  ThumbsUp,
  ThumbsDown,
  PencilSimple,
  Receipt,
  ClockCounterClockwise
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// Tab configuration
const TABS = [
  { id: 'overview', label: 'Overview', icon: House },
  { id: 'timeline', label: 'Timeline', icon: Calendar },
  { id: 'photos', label: 'Photos', icon: Camera },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'costs', label: 'Costs', icon: CurrencyDollar },
  { id: 'workers', label: 'Team', icon: Users },
  { id: 'messages', label: 'Messages', icon: ChatCircle },
  { id: 'changes', label: 'Changes', icon: PencilSimple },
];

const ClientPortal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || localStorage.getItem('clientToken');
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [clientData, setClientData] = useState(null);
  const [project, setProject] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [permissions, setPermissions] = useState({});
  
  // Tab data states
  const [timeline, setTimeline] = useState({ milestones: [], escrow_milestones: [] });
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [costs, setCosts] = useState({ costs: [], escrow: null, summary: {} });
  const [workers, setWorkers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  
  // UI states
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showChangeOrderModal, setShowChangeOrderModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  
  // Form states
  const [accountPassword, setAccountPassword] = useState('');
  const [changeOrder, setChangeOrder] = useState({ title: '', description: '', budget: '', timeline: '' });
  const [review, setReview] = useState({ rating: 5, text: '', recommend: true });

  useEffect(() => {
    if (token) {
      localStorage.setItem('clientToken', token);
      fetchPortalAccess();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [token]);

  useEffect(() => {
    if (project && token) {
      fetchTabData(activeTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [activeTab, project]);

  const fetchPortalAccess = async () => {
    try {
      const response = await axios.get(`${API}/api/client-portal/access?token=${token}`);
      setClientData(response.data.client);
      setProject(response.data.project);
      setContractor(response.data.contractor);
      setPermissions(response.data.client.permissions || {});
    } catch (error) {
      toast({
        title: 'Access Denied',
        description: 'Invalid or expired access link',
        variant: 'destructive'
      });
      localStorage.removeItem('clientToken');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async (tab) => {
    if (!token) return;
    
    try {
      switch (tab) {
        case 'timeline':
          const timelineRes = await axios.get(`${API}/api/client-portal/project/${token}/timeline`);
          setTimeline(timelineRes.data);
          break;
        case 'photos':
          const photosRes = await axios.get(`${API}/api/client-portal/project/${token}/photos`);
          setPhotos(photosRes.data.photos || []);
          break;
        case 'documents':
          const docsRes = await axios.get(`${API}/api/client-portal/project/${token}/documents`);
          setDocuments(docsRes.data.documents || []);
          break;
        case 'costs':
          const costsRes = await axios.get(`${API}/api/client-portal/project/${token}/cost-breakdown`);
          setCosts(costsRes.data);
          break;
        case 'workers':
          const workersRes = await axios.get(`${API}/api/client-portal/project/${token}/workers`);
          setWorkers(workersRes.data.workers || []);
          const logsRes = await axios.get(`${API}/api/client-portal/project/${token}/daily-logs`);
          setDailyLogs(logsRes.data.logs || []);
          break;
        case 'messages':
          const msgsRes = await axios.get(`${API}/api/client-portal/project/${token}/messages`);
          setMessages(msgsRes.data.messages || []);
          break;
        case 'changes':
          const changesRes = await axios.get(`${API}/api/client-portal/project/${token}/change-orders`);
          setChangeOrders(changesRes.data.change_orders || []);
          break;
      }
    } catch (error) {
      console.error(`Failed to fetch ${tab} data:`, error);
    }
  };

  const handleCreateAccount = async () => {
    if (accountPassword.length < 6) {
      toast({ title: 'Password too short', description: 'Use at least 6 characters', variant: 'destructive' });
      return;
    }
    
    try {
      await axios.post(`${API}/api/client-portal/create-account`, {
        token,
        password: accountPassword
      });
      toast({ title: 'Account created!', description: 'You can now log in with your email and password' });
      setShowCreateAccount(false);
      setClientData(prev => ({ ...prev, has_account: true }));
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create account', variant: 'destructive' });
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/api/client-portal/project/${token}/messages`, {
        message: newMessage
      });
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
      toast({ title: 'Message sent' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleApproveMilestone = async (milestoneId, approved, feedback = '') => {
    try {
      await axios.post(`${API}/api/client-portal/project/${token}/milestone/${milestoneId}/approve`, {
        approved,
        feedback
      });
      toast({ title: approved ? 'Milestone Approved!' : 'Revision Requested' });
      fetchTabData('timeline');
      fetchTabData('costs');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update milestone', variant: 'destructive' });
    }
  };

  const handleSubmitChangeOrder = async () => {
    if (!changeOrder.title || !changeOrder.description) {
      toast({ title: 'Missing fields', variant: 'destructive' });
      return;
    }
    
    try {
      await axios.post(`${API}/api/client-portal/project/${token}/change-order`, {
        title: changeOrder.title,
        description: changeOrder.description,
        requested_budget_change: changeOrder.budget ? parseFloat(changeOrder.budget) : null,
        requested_timeline_change: changeOrder.timeline ? parseInt(changeOrder.timeline) : null
      });
      toast({ title: 'Change order submitted' });
      setShowChangeOrderModal(false);
      setChangeOrder({ title: '', description: '', budget: '', timeline: '' });
      fetchTabData('changes');
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleSubmitReview = async () => {
    if (!review.text.trim()) {
      toast({ title: 'Please write a review', variant: 'destructive' });
      return;
    }
    
    try {
      await axios.post(`${API}/api/client-portal/project/${token}/review`, {
        rating: review.rating,
        review_text: review.text,
        would_recommend: review.recommend
      });
      toast({ title: 'Review submitted!', description: 'Thank you for your feedback' });
      setShowReviewModal(false);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit review', variant: 'destructive' });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clientToken');
    navigate('/client-login');
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-100 text-blue-700',
      'in_progress': 'bg-amber-100 text-amber-700',
      'completed': 'bg-green-100 text-green-700',
      'cancelled': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <SpinnerGap size={48} className="animate-spin text-[#2B4C6F] mx-auto mb-4" />
          <p className="text-neutral-600 font-medium">Loading your project portal...</p>
        </div>
      </div>
    );
  }

  if (!token || !project) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border-2 border-black shadow-[6px_6px_0px_#000] p-8 max-w-md w-full text-center">
          <ShieldCheck size={64} className="text-[#2B4C6F] mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-2">Client Portal</h1>
          <p className="text-neutral-600 mb-6">
            Please use the link sent to your email to access your project portal.
          </p>
          <Button
            onClick={() => navigate('/client-login')}
            className="w-full bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000]"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="bg-white border-b-2 border-black shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2B4C6F] rounded-xl flex items-center justify-center border-2 border-black">
                <Handshake size={28} className="text-white" weight="bold" />
              </div>
              <div>
                <h1 className="font-black text-xl text-neutral-900">{project.title}</h1>
                <p className="text-sm text-neutral-500 flex items-center gap-1">
                  <MapPin size={14} /> {project.location}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="font-bold text-neutral-900">{clientData?.name}</p>
                <p className="text-xs text-neutral-500">{clientData?.email}</p>
              </div>
              
              {!clientData?.has_account && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreateAccount(true)}
                  className="border-2 border-[#2B4C6F] text-[#2B4C6F]"
                >
                  <Lock size={16} className="mr-1" /> Create Account
                </Button>
              )}
              
              {permissions.leave_reviews && (
                <Button
                  size="sm"
                  onClick={() => setShowReviewModal(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white border-2 border-black shadow-[2px_2px_0px_#000]"
                >
                  <Star size={16} className="mr-1" weight="fill" /> Review
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-neutral-500"
              >
                <SignOut size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            // Check permission
            const permKey = `view_${tab.id === 'overview' ? 'status' : tab.id === 'costs' ? 'cost_breakdown' : tab.id === 'workers' ? 'workers' : tab.id}`;
            if (tab.id !== 'overview' && tab.id !== 'messages' && tab.id !== 'changes' && !permissions[permKey]) {
              return null;
            }
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-[#2B4C6F] text-white border-2 border-black shadow-[2px_2px_0px_#000]'
                    : 'bg-white text-neutral-700 border-2 border-neutral-300 hover:border-black'
                }`}
              >
                <Icon size={20} weight={isActive ? 'bold' : 'regular'} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid md:grid-cols-3 gap-6">
              {/* Project Status */}
              <div className="md:col-span-2 bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                <h2 className="font-black text-xl mb-4 flex items-center gap-2">
                  <House size={24} className="text-[#2B4C6F]" />
                  Project Overview
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-600">Status</span>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-600">Budget</span>
                    <span className="font-bold">${project.budget?.toLocaleString()}</span>
                  </div>
                  
                  {project.start_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-600">Start Date</span>
                      <span className="font-medium">{new Date(project.start_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {project.deadline && (
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-600">Target Completion</span>
                      <span className="font-medium">{new Date(project.deadline).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  <div className="pt-4 border-t">
                    <p className="text-neutral-600 text-sm">{project.description}</p>
                  </div>
                </div>
              </div>

              {/* Contractor Info */}
              <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                <h2 className="font-black text-xl mb-4 flex items-center gap-2">
                  <User size={24} className="text-[#2B4C6F]" />
                  Your Contractor
                </h2>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-neutral-500">Name</p>
                    <p className="font-bold text-lg">{contractor?.name}</p>
                  </div>
                  
                  {contractor?.company && (
                    <div>
                      <p className="text-sm text-neutral-500">Company</p>
                      <p className="font-medium">{contractor.company}</p>
                    </div>
                  )}
                  
                  {contractor?.email && (
                    <div className="flex items-center gap-2 text-neutral-600">
                      <Envelope size={16} />
                      <span className="text-sm">{contractor.email}</span>
                    </div>
                  )}
                  
                  {contractor?.phone && (
                    <div className="flex items-center gap-2 text-neutral-600">
                      <Phone size={16} />
                      <span className="text-sm">{contractor.phone}</span>
                    </div>
                  )}
                  
                  {permissions.send_messages && (
                    <Button
                      onClick={() => setActiveTab('messages')}
                      className="w-full mt-4 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
                    >
                      <ChatCircle size={20} className="mr-2" />
                      Send Message
                    </Button>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              {(permissions.request_changes || permissions.approve_milestones) && (
                <div className="md:col-span-3 bg-gradient-to-r from-[#2B4C6F] to-[#203A55] rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6 text-white">
                  <h3 className="font-bold text-lg mb-4">Quick Actions</h3>
                  <div className="flex flex-wrap gap-3">
                    {permissions.request_changes && (
                      <Button
                        onClick={() => setShowChangeOrderModal(true)}
                        className="bg-white text-[#2B4C6F] hover:bg-neutral-100 font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
                      >
                        <Plus size={20} className="mr-2" />
                        Request Change
                      </Button>
                    )}
                    <Button
                      onClick={() => setActiveTab('timeline')}
                      variant="outline"
                      className="border-2 border-white text-white hover:bg-white/10 font-bold"
                    >
                      <Calendar size={20} className="mr-2" />
                      View Milestones
                    </Button>
                    <Button
                      onClick={() => setActiveTab('photos')}
                      variant="outline"
                      className="border-2 border-white text-white hover:bg-white/10 font-bold"
                    >
                      <Camera size={20} className="mr-2" />
                      View Photos
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
              <h2 className="font-black text-xl mb-6 flex items-center gap-2">
                <Calendar size={24} className="text-[#2B4C6F]" />
                Project Timeline & Milestones
              </h2>
              
              {timeline.escrow_milestones?.length > 0 ? (
                <div className="space-y-4">
                  {timeline.escrow_milestones.map((milestone, idx) => (
                    <div 
                      key={milestone.id || idx}
                      className={`border-2 rounded-xl overflow-hidden ${
                        milestone.client_approved ? 'border-green-400 bg-green-50' :
                        milestone.status === 'completed' ? 'border-amber-400 bg-amber-50' :
                        'border-neutral-300'
                      }`}
                    >
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedMilestone(expandedMilestone === idx ? null : idx)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                              milestone.client_approved ? 'bg-green-500 text-white' :
                              milestone.status === 'completed' ? 'bg-amber-500 text-white' :
                              'bg-neutral-200 text-neutral-600'
                            }`}>
                              {milestone.client_approved ? <CheckCircle size={20} weight="bold" /> : idx + 1}
                            </div>
                            <div>
                              <p className="font-bold text-lg">{milestone.title || milestone.description}</p>
                              <p className="text-sm text-neutral-500">
                                ${milestone.amount?.toLocaleString()} • Due: {milestone.due_date ? new Date(milestone.due_date).toLocaleDateString() : 'TBD'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge className={
                              milestone.client_approved ? 'bg-green-100 text-green-700' :
                              milestone.status === 'completed' ? 'bg-amber-100 text-amber-700' :
                              milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-neutral-100 text-neutral-600'
                            }>
                              {milestone.client_approved ? 'Approved' : milestone.status?.replace('_', ' ') || 'Pending'}
                            </Badge>
                            {expandedMilestone === idx ? <CaretUp size={20} /> : <CaretDown size={20} />}
                          </div>
                        </div>
                      </div>
                      
                      {expandedMilestone === idx && (
                        <div className="border-t-2 border-current/20 p-4 bg-white/50">
                          {milestone.description && (
                            <p className="text-neutral-600 mb-4">{milestone.description}</p>
                          )}
                          
                          {milestone.status === 'completed' && !milestone.client_approved && permissions.approve_milestones && (
                            <div className="flex gap-3">
                              <Button
                                onClick={() => handleApproveMilestone(milestone.id, true)}
                                className="bg-green-500 hover:bg-green-600 text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
                              >
                                <ThumbsUp size={18} className="mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleApproveMilestone(milestone.id, false, 'Needs revision')}
                                variant="outline"
                                className="border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold"
                              >
                                <ThumbsDown size={18} className="mr-2" />
                                Request Revision
                              </Button>
                            </div>
                          )}
                          
                          {milestone.client_feedback && (
                            <p className="mt-3 text-sm text-neutral-500 italic">
                              Your feedback: "{milestone.client_feedback}"
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-500">
                  <Calendar size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No milestones have been set up yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
              <h2 className="font-black text-xl mb-6 flex items-center gap-2">
                <Camera size={24} className="text-[#2B4C6F]" />
                Project Photos ({photos.length})
              </h2>
              
              {photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div 
                      key={photo._id}
                      className="aspect-square rounded-lg border-2 border-neutral-200 overflow-hidden hover:border-[#2B4C6F] transition-colors cursor-pointer group relative"
                    >
                      <img 
                        src={photo.url || photo.image_url} 
                        alt={photo.caption || 'Project photo'}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye size={32} className="text-white" />
                      </div>
                      {photo.caption && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 truncate">
                          {photo.caption}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-500">
                  <Camera size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No photos have been uploaded yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
              <h2 className="font-black text-xl mb-6 flex items-center gap-2">
                <FileText size={24} className="text-[#2B4C6F]" />
                Project Documents ({documents.length})
              </h2>
              
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div 
                      key={doc._id}
                      className="flex items-center justify-between p-4 border-2 border-neutral-200 rounded-lg hover:border-[#2B4C6F] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={24} className="text-neutral-400" />
                        <div>
                          <p className="font-bold">{doc.title || doc.original_filename}</p>
                          <p className="text-xs text-neutral-500">
                            {doc.category} • {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${API}/api/job-documents/document/${doc._id}/download`, '_blank')}
                      >
                        <Download size={20} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-500">
                  <FileText size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No documents have been shared yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Costs Tab */}
          {activeTab === 'costs' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                  <p className="text-sm text-neutral-500 mb-1">Total Budget</p>
                  <p className="text-3xl font-black text-neutral-900">
                    ${(costs.summary?.total_budget || project.budget || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                  <p className="text-sm text-neutral-500 mb-1">Amount Spent</p>
                  <p className="text-3xl font-black text-amber-600">
                    ${(costs.summary?.total_spent || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                  <p className="text-sm text-neutral-500 mb-1">Remaining</p>
                  <p className="text-3xl font-black text-green-600">
                    ${(costs.summary?.remaining || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Escrow Status */}
              {costs.escrow && (
                <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                  <h3 className="font-black text-lg mb-4 flex items-center gap-2">
                    <ShieldCheck size={24} className="text-green-600" />
                    Escrow Protection
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Total in Escrow</span>
                      <span className="font-bold">${costs.escrow.total_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Funded</span>
                      <span className="font-bold text-green-600">${costs.escrow.funded_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Released to Contractor</span>
                      <span className="font-bold text-blue-600">${costs.escrow.released_amount?.toLocaleString()}</span>
                    </div>
                    
                    <Progress 
                      value={(costs.escrow.released_amount / costs.escrow.total_amount) * 100} 
                      className="h-3"
                    />
                    <p className="text-xs text-neutral-500 text-center">
                      {Math.round((costs.escrow.released_amount / costs.escrow.total_amount) * 100)}% of funds released
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Workers/Team Tab */}
          {activeTab === 'workers' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Team Members */}
              <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                <h2 className="font-black text-xl mb-4 flex items-center gap-2">
                  <Users size={24} className="text-[#2B4C6F]" />
                  Team Members ({workers.length})
                </h2>
                
                {workers.length > 0 ? (
                  <div className="space-y-3">
                    {workers.map((worker) => (
                      <div key={worker.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                        <div className="w-12 h-12 rounded-full bg-[#2B4C6F] flex items-center justify-center text-white font-bold">
                          {worker.name?.charAt(0) || 'W'}
                        </div>
                        <div>
                          <p className="font-bold">{worker.name}</p>
                          <p className="text-sm text-neutral-500">
                            {worker.trade?.join(', ') || 'Worker'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-neutral-500 text-center py-6">No workers assigned yet.</p>
                )}
              </div>

              {/* Daily Logs */}
              <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
                <h2 className="font-black text-xl mb-4 flex items-center gap-2">
                  <ClockCounterClockwise size={24} className="text-[#2B4C6F]" />
                  Recent Activity
                </h2>
                
                {dailyLogs.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {dailyLogs.map((log) => (
                      <div key={log._id} className="p-3 border-l-4 border-[#2B4C6F] bg-neutral-50 rounded-r-lg">
                        <div className="flex justify-between">
                          <p className="font-bold text-sm">{log.worker_name}</p>
                          <p className="text-xs text-neutral-500">
                            {new Date(log.clock_in_time).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="text-sm text-neutral-600">
                          {log.hours ? `${log.hours.toFixed(1)} hours worked` : 'Time logged'}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-neutral-500 text-center py-6">No activity logged yet.</p>
                )}
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] overflow-hidden">
              <div className="bg-[#2B4C6F] text-white p-4">
                <h2 className="font-black text-xl flex items-center gap-2">
                  <ChatCircle size={24} />
                  Messages with {contractor?.name}
                </h2>
              </div>
              
              {/* Messages List */}
              <div className="h-96 overflow-y-auto p-4 space-y-4 bg-neutral-50">
                {messages.length > 0 ? (
                  messages.map((msg) => (
                    <div 
                      key={msg._id}
                      className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] p-3 rounded-xl ${
                        msg.sender_type === 'client' 
                          ? 'bg-[#2B4C6F] text-white rounded-br-none' 
                          : 'bg-white border-2 border-neutral-200 rounded-bl-none'
                      }`}>
                        <p className="text-sm">{msg.message}</p>
                        <p className={`text-xs mt-1 ${msg.sender_type === 'client' ? 'text-white/70' : 'text-neutral-400'}`}>
                          {new Date(msg.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-neutral-500">
                    <ChatCircle size={48} className="mx-auto mb-3 opacity-50" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                )}
              </div>
              
              {/* Message Input */}
              {permissions.send_messages && (
                <div className="p-4 border-t-2 border-neutral-200">
                  <div className="flex gap-3">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 border-2 border-neutral-300 resize-none"
                      rows={2}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      className="bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000] px-6"
                    >
                      {sendingMessage ? <SpinnerGap className="animate-spin" size={20} /> : <PaperPlaneTilt size={20} weight="bold" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Change Orders Tab */}
          {activeTab === 'changes' && (
            <div className="bg-white rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-black text-xl flex items-center gap-2">
                  <PencilSimple size={24} className="text-[#2B4C6F]" />
                  Change Orders
                </h2>
                {permissions.request_changes && (
                  <Button
                    onClick={() => setShowChangeOrderModal(true)}
                    className="bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
                  >
                    <Plus size={20} className="mr-2" />
                    New Request
                  </Button>
                )}
              </div>
              
              {changeOrders.length > 0 ? (
                <div className="space-y-4">
                  {changeOrders.map((order) => (
                    <div key={order._id} className="border-2 border-neutral-200 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-bold text-lg">{order.title}</p>
                          <p className="text-neutral-600 text-sm mt-1">{order.description}</p>
                          {(order.requested_budget_change || order.requested_timeline_change) && (
                            <div className="flex gap-4 mt-2 text-sm">
                              {order.requested_budget_change && (
                                <span className="text-amber-600">
                                  Budget: ${order.requested_budget_change?.toLocaleString()}
                                </span>
                              )}
                              {order.requested_timeline_change && (
                                <span className="text-blue-600">
                                  Timeline: {order.requested_timeline_change} days
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <Badge className={
                          order.status === 'approved' ? 'bg-green-100 text-green-700' :
                          order.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {order.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-neutral-400 mt-3">
                        Submitted: {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-500">
                  <PencilSimple size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No change orders submitted.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Account Modal */}
      {showCreateAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black shadow-[6px_6px_0px_#000] p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-black text-xl mb-4">Create Your Account</h3>
            <p className="text-neutral-600 mb-4">
              Create a password to easily log in without needing the email link.
            </p>
            <Input
              type="password"
              placeholder="Create a password (min 6 characters)"
              value={accountPassword}
              onChange={(e) => setAccountPassword(e.target.value)}
              className="mb-4 border-2 border-black"
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowCreateAccount(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAccount}
                className="flex-1 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
              >
                Create Account
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change Order Modal */}
      {showChangeOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black shadow-[6px_6px_0px_#000] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-black text-xl">Request a Change</h3>
              <button onClick={() => setShowChangeOrderModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Title *</label>
                <Input
                  value={changeOrder.title}
                  onChange={(e) => setChangeOrder({...changeOrder, title: e.target.value})}
                  placeholder="e.g., Add extra outlet in kitchen"
                  className="border-2 border-black"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Description *</label>
                <Textarea
                  value={changeOrder.description}
                  onChange={(e) => setChangeOrder({...changeOrder, description: e.target.value})}
                  placeholder="Describe the change you'd like..."
                  className="border-2 border-black"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">Budget Impact ($)</label>
                  <Input
                    type="number"
                    value={changeOrder.budget}
                    onChange={(e) => setChangeOrder({...changeOrder, budget: e.target.value})}
                    placeholder="Optional"
                    className="border-2 border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">Timeline Impact (days)</label>
                  <Input
                    type="number"
                    value={changeOrder.timeline}
                    onChange={(e) => setChangeOrder({...changeOrder, timeline: e.target.value})}
                    placeholder="Optional"
                    className="border-2 border-black"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowChangeOrderModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitChangeOrder}
                className="flex-1 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black shadow-[6px_6px_0px_#000] p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-black text-xl">Leave a Review</h3>
              <button onClick={() => setShowReviewModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Star Rating */}
              <div>
                <label className="block text-sm font-bold mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReview({...review, rating: star})}
                      className="p-1"
                    >
                      <Star 
                        size={32} 
                        weight={star <= review.rating ? 'fill' : 'regular'}
                        className={star <= review.rating ? 'text-amber-400' : 'text-neutral-300'}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Review Text */}
              <div>
                <label className="block text-sm font-bold mb-1">Your Review</label>
                <Textarea
                  value={review.text}
                  onChange={(e) => setReview({...review, text: e.target.value})}
                  placeholder="Share your experience..."
                  className="border-2 border-black"
                  rows={4}
                />
              </div>
              
              {/* Recommend */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="recommend"
                  checked={review.recommend}
                  onChange={(e) => setReview({...review, recommend: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="recommend" className="font-medium">
                  I would recommend this contractor
                </label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowReviewModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitReview}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
              >
                <Star size={20} className="mr-2" weight="fill" />
                Submit Review
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortal;
